import React, { ComponentPropsWithoutRef } from 'react';
import styled from 'styled-components';
import { SystemIconButton } from '../Icon/SystemIconButton';
import { SystemIcon } from '../Icon';
import { tokens } from '../../theme';

interface FilePreviewProps extends ComponentPropsWithoutRef<'div'> {
  file: { filename?: string; name?: string } | undefined | null;
  onDelete?(): void;
  progress?: number;
  dataTest?: string;
}

export const FilePreview: React.FC<React.PropsWithChildren<FilePreviewProps>> = ({
  file,
  onDelete,
  dataTest,
  progress,
  ...props
}) => {
  const inProgress = typeof progress === 'number' && progress > 0;
  return (
    <StyledFilePreview {...props} data-test={dataTest} aria-label={file?.filename ?? file?.name} role="region">
      <StyledContent>
        <StyledFileName>
          <SystemIcon name="attach_file" style={{ marginRight: 12 }} color={tokens.colorOnSurfaceVariant} />
          <StyledText data-test="filename">{file?.filename ?? file?.name}</StyledText>
        </StyledFileName>
        <StyledIconContainer>
          {!!onDelete && !inProgress && (
            <SystemIconButton iconName="delete" disabled={inProgress} onClick={() => onDelete()} />
          )}
        </StyledIconContainer>
      </StyledContent>
      <StyledProgress $progress={progress ?? 0} />
    </StyledFilePreview>
  );
};

const StyledIconContainer = styled.div`
  min-height: 48px;
  display: inline-block;
`;
const StyledFilePreview = styled.div`
  width: 80%;
  padding: ${({ theme: { spacingSize } }) => `${spacingSize.m} ${spacingSize.m}`};
  background-color: ${(props) => props.theme.colorSurface};
  color: ${(props) => props.theme.colorOnSurface};
  border: 1px solid ${(props) => props.theme.colorSurfaceVariant};
  border-radius: 4px;
  position: relative;
  overflow: hidden;
`;

const StyledContent = styled.div`
  justify-content: space-between;
  display: flex;
  align-items: center;
  position: relative;
  z-index: 2;
`;

const StyledProgress = styled.div<{ $progress: number }>`
  transition: width 0.2s ease-in-out;
  position: absolute;
  background-color: ${(props) => props.theme.colorPrimaryContainer};
  width: ${({ $progress }) => $progress}%;
  top: 0;
  left: 0;
  bottom: 0;
`;

const StyledFileName = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`;

const StyledText = styled.div`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;
